import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ChatConversationItem } from '@ayub/shared'
import { ChevronLeft, Loader2, MessageCircle, Send } from '@/lib/icons'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import { formatRelativeUz } from '@/lib/relative-date'
import { sendChatMessage } from './api'
import { useChatMessages, useConversations } from './hooks'

/**
 * Chat bo'limi — chapda suhbatlar ro'yxati (o'qilmagan belgisi bilan), o'ngda tanlangan suhbat
 * (xabarlar + yozish). Mobil ekranda bittasi ko'rinadi: suhbat tanlansa ro'yxat o'rniga chat
 * ochiladi, "orqaga" tugmasi bilan qaytiladi.
 *
 * `initialBloggerId` — bloger kartochkasidagi "Chat" tugmasidan kelganda oldindan tanlanadi.
 */
export function ChatPage({ initialBloggerId }: { initialBloggerId: number | null }) {
  const [selectedId, setSelectedId] = useState<number | null>(initialBloggerId)

  // Kartochkadan yangi bloger tanlab kelinganda — o'sha suhbatga o'tamiz.
  useEffect(() => {
    if (initialBloggerId !== null) setSelectedId(initialBloggerId)
  }, [initialBloggerId])

  const { conversations, isLoading } = useConversations(true)

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <MessageCircle size={20} aria-hidden="true" /> Chat
        </h1>
      </header>

      <div className="flex h-[calc(100dvh-11rem)] overflow-hidden rounded-card border border-app-border">
        {/* Suhbatlar ro'yxati */}
        <aside
          className={`w-full shrink-0 overflow-y-auto border-app-border md:w-72 md:border-r ${
            selectedId !== null ? 'hidden md:block' : 'block'
          }`}
        >
          {isLoading ? (
            <div className="flex flex-col gap-2 p-3">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-sm text-app-hint">Hali suhbat yo&apos;q</p>
          ) : (
            <ul className="flex flex-col">
              {conversations.map((c) => (
                <ConversationRow
                  key={c.bloggerId}
                  conversation={c}
                  active={c.bloggerId === selectedId}
                  onSelect={() => setSelectedId(c.bloggerId)}
                />
              ))}
            </ul>
          )}
        </aside>

        {/* Tanlangan suhbat */}
        <section
          className={`min-w-0 flex-1 flex-col ${selectedId !== null ? 'flex' : 'hidden md:flex'}`}
        >
          {selectedId === null ? (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-app-hint">
              Suhbatni tanlang
            </div>
          ) : (
            <ChatThread
              bloggerId={selectedId}
              conversation={conversations.find((c) => c.bloggerId === selectedId) ?? null}
              onBack={() => setSelectedId(null)}
            />
          )}
        </section>
      </div>
    </div>
  )
}

function ConversationRow({
  conversation,
  active,
  onSelect,
}: {
  conversation: ChatConversationItem
  active: boolean
  onSelect: () => void
}) {
  const preview = (conversation.lastSender === 'ADMIN' ? 'Siz: ' : '') + conversation.lastText
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full flex-col gap-0.5 border-b border-app-border px-3 py-2.5 text-left transition-colors ${
          active ? 'bg-brand-subtle' : 'hover:bg-app-card'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-app-text">
            {conversation.fullName}
          </span>
          {conversation.unreadCount > 0 && (
            <span className="inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-brand px-1.5 text-xs font-semibold text-white">
              {conversation.unreadCount}
            </span>
          )}
        </div>
        <span className="truncate text-xs text-app-hint">{preview}</span>
        <span className="text-[11px] text-app-hint">{formatRelativeUz(conversation.lastAt)}</span>
      </button>
    </li>
  )
}

function ChatThread({
  bloggerId,
  conversation,
  onBack,
}: {
  bloggerId: number
  conversation: ChatConversationItem | null
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const { messages, isLoading } = useChatMessages(bloggerId)
  const [text, setText] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  const mutation = useMutation({
    mutationFn: (value: string) => sendChatMessage(bloggerId, value),
    onSuccess: () => {
      setText('')
      void queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'chat',
      })
    },
  })

  // Yangi xabar kelganda pastga aylantiramiz.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length])

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const value = text.trim()
    if (value.length === 0 || mutation.isPending) return
    mutation.mutate(value)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Sarlavha */}
      <div className="flex shrink-0 items-center gap-2 border-b border-app-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label="Orqaga"
          className="flex size-8 items-center justify-center rounded-button text-app-hint hover:bg-app-card md:hidden"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-app-text">
            {conversation?.fullName ?? 'Suhbat'}
          </p>
          {conversation && (
            <p className="truncate text-xs text-app-hint">@{conversation.instagramUsername}</p>
          )}
        </div>
      </div>

      {/* Xabarlar */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-app-bg/40 p-3">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-2/3" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-app-hint">
            Hali xabar yo&apos;q. Birinchi bo&apos;lib yozing.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender === 'ADMIN'
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-card px-3 py-2 text-sm ${
                    mine ? 'bg-brand text-white' : 'bg-app-card text-app-text'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <p className={`mt-1 text-[11px] ${mine ? 'text-white/70' : 'text-app-hint'}`}>
                    {mine && m.authorName ? `${m.authorName} · ` : ''}
                    {formatRelativeUz(m.createdAt)}
                  </p>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Yozish */}
      <form
        onSubmit={handleSubmit}
        className="flex shrink-0 items-end gap-2 border-t border-app-border p-3"
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit(e)
            }
          }}
          rows={1}
          placeholder="Xabar yozing..."
          className="max-h-32 min-h-11 flex-1 resize-none rounded-input border border-app-border bg-app-bg px-3 py-2.5 text-base text-app-text outline-none focus-visible:border-brand"
        />
        <Button type="submit" disabled={text.trim().length === 0 || mutation.isPending}>
          {mutation.isPending ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Send size={16} aria-hidden="true" />
          )}
        </Button>
      </form>

      {mutation.isError && (
        <p className="px-3 pb-2 text-xs text-stage-rejected-text">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Yuborib bo‘lmadi'}
        </p>
      )}
    </div>
  )
}
